import { useEffect, useState } from "react";
import { motion } from "framer-motion";

// ScrollProgress: A thin progress bar at the top that indicates scroll position
const ScrollProgress = () => {
  const [progress, setProgress] = useState(0);
  useEffect(() => {
    const handler = () => {
      const total = document.body.scrollHeight - window.innerHeight;
      setProgress((window.scrollY / total) * 100);
    };
    window.addEventListener("scroll", handler);
    return () => window.removeEventListener("scroll", handler);
  }, []);
  return (
    <div className="fixed top-0 left-0 w-full h-1 bg-blue-200 z-50">
      <div
        className="h-full bg-blue-600 transition-all duration-200"
        style={{ width: `${progress}%` }}
      />
    </div>
  );
};

// SectionCard: Animated card wrapper for each section (h3 + content)
const SectionCard = ({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    whileHover={{ scale: 1.03, y: -5 }}
    transition={{
      duration: 0.4,
      ease: "easeOut",
      type: "spring",
      stiffness: 200,
      damping: 20,
    }}
    viewport={{ once: true, amount: 0.2 }}
    className="w-full p-6 bg-white/80 backdrop-blur-sm shadow-lg border border-blue-100 rounded mb-6"
  >
    <h3 className="text-xl font-bold text-blue-900 mb-3">{title}</h3>
    {children}
  </motion.div>
);

// Subheading: Style for subheadings inside cards
const Subheading = ({ children }: { children: React.ReactNode }) => (
  <h4 className="text-red-700 font-semibold text-xl mt-2.5 mb-1">{children}</h4>
);

// Paragraph: Styled paragraph for consistent text appearance
const Paragraph = ({
  children,
  className = "",
}: {
  children: React.ReactNode;
  className?: string;
}) => (
  <p
    className={`text-lg text-gray-800 leading-relaxed text-justify ${className}`}
  >
    {children}
  </p>
);

const SupplyControl = () => (
  <div className="relative overflow-hidden">
    {/* Render scroll progress bar and background animation */}
    <ScrollProgress />

    <div className="space-y-8 px-4 py-10 md:py-6 md:px-8">
      {/* Introduction: Animated into view */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{
          duration: 0.5,
          ease: "easeOut",
          type: "spring",
          stiffness: 200,
          damping: 20,
        }}
        viewport={{ once: true }}
        className="max-w-6xl mx-auto space-y-4 text-center"
      >
        <h2 className="text-3xl font-bold text-primary-dark">
          Supply Control and Triple-Layer Security in UcWaves
        </h2>
        <Paragraph className="text-gray-700 max-w-6xl mx-auto">
          Uncensored Waves (UcWaves) architected its token system with rigorous
          controls to safeguard token integrity and trust. The following
          outlines our triple-layer security model centered on a fixed NAU
          supply, a one-way burn-to-mint mechanism for opinion tokens, and
          role-based access via a dedicated Controller contract. Combined with
          the ERC-20 allowance pattern and OpenZeppelin’s security primitives,
          these layers ensure transparent, auditable, and
          unauthorized-action-resistant token operations.
        </Paragraph>
      </motion.div>

      {/* Two-column layout for the main sections */}
      <div className="flex flex-col md:flex-row gap-6 max-w-6xl mx-auto">
        {/* Left column */}
        <div className="flex-1 flex flex-col">
          {/* Section 1 */}
          <SectionCard title="1. Fixed Supply of NAU">
            <Subheading>1.1 Immutable Cap on NAU</Subheading>
            <ul className="list-disc list-inside text-base text-gray-800 mb-2">
              <li>
                Pre-Minted on Deployment: Total NAU supply minted once and
                allocated to initial addresses (Treasury, Team, Community Pool).
              </li>
              <br />
              <li>
                Immutable Cap Enforcement: No mint() function exists, preventing
                any post-deployment inflation.
              </li>
            </ul>
            <Subheading>1.2 Security Benefits</Subheading>
            <ul className="list-disc list-inside text-base text-gray-800">
              <li>
                Predictable Tokenomics: Known maximum supply enables accurate
                scarcity modeling and governance planning.
              </li>
              <br />
              <li>
                Immutable on-Chain Record: Bytecode permanently excludes mint
                paths, ensuring future upgrades cannot inflate NAU.
              </li>
            </ul>
          </SectionCard>

          {/* Section 2 */}
          <SectionCard title="2. One-Way Burn-to-Mint Mechanism">
            <Subheading>2.1 Controlled Minting of Opinion Tokens</Subheading>
            <ul className="list-disc list-inside text-base text-gray-800 mb-2">
              <li>
                Minting Only via transformX(): NAUY and NAUN minted internally
                after NAU burn.
              </li>
              <br />
              <li>
                Fixed-Input Enforcement: Each NAU burned yields precisely
                computed opinion tokens based on TWAP.
              </li>
            </ul>
            <Subheading>2.2 One-Way Conversion Guarantee</Subheading>
            <ul className="list-disc list-inside text-base text-gray-800 mb-2">
              <li>
                Burn-Only Input: No public mint() for NAU, and no public burn()
                for opinion tokens.
              </li>
              <br />
              <li>
                Atomic Execution: Burn and mint occur in the same transaction
                via burnFrom() and controllerMint().
              </li>
            </ul>
            <Subheading>2.3 Security Advantages</Subheading>
            <ul className="list-disc list-inside text-base text-gray-800">
              <li>
                Supply Balance: Total (NAU + NAUY + NAUN) traceable to initial
                NAU supply.
              </li>
              <br />
              <li>
                Transparent Audit Trail: Transfer to zero address and Mint
                events provide full on-chain history.
              </li>
            </ul>
          </SectionCard>
        </div>

        {/* Right column */}
        <div className="flex-1 flex flex-col">
          {/* Section 3 */}
          <SectionCard title="3. Role-Based Access via Controller">
            <Subheading>3.1 Dedicated Burner Role</Subheading>
            <ul className="list-disc list-inside text-base text-gray-800 mb-2">
              <li>
                BURNER_ROLE Assignment: Only the Controller contract holds this
                role in NAU’s AccessControl.
              </li>
              <br />
              <li>
                Immutable Role Binding: Role assignments locked after audits,
                preventing future grants.
              </li>
            </ul>
            <Subheading>3.2 Controller as Single Point of Authority</Subheading>
            <ul className="list-disc list-inside text-base text-gray-800 mb-2">
              <li>
                Atomic Operations: Controller handles burning, oracle reads, and
                minting within one trust boundary.
              </li>
              <br />
              <li>
                Access-Controlled Functions: Privileged functions restricted
                with onlyRole(BURNER_ROLE).
              </li>
            </ul>
            <Subheading>3.3 Security Outcomes</Subheading>
            <ul className="list-disc list-inside text-base text-gray-800">
              <li>
                Minimized Attack Surface: Only Controller’s audited code can
                mint or burn.
              </li>
              <br />
              <li>
                Governance Path: Future Controller upgrades require community
                proposals.
              </li>
            </ul>
          </SectionCard>

          {/* Section 4 */}
          <SectionCard title="4. ERC-20 Allowance Pattern Integration">
            <Subheading>4.1 Approvals and transferFrom()</Subheading>
            <ul className="list-disc list-inside text-base text-gray-800 mb-2">
              <li>
                approve(): Users grant Controller permission to spend tokens.
              </li>
              <br />
              <li>
                safeTransferFrom(): Controller pulls tokens only after allowance
                is set.
              </li>
            </ul>
            <Subheading>4.2 SafeERC20 for Robustness</Subheading>
            <ul className="list-disc list-inside text-base text-gray-800">
              <li>
                Failure Reversion: safeTransferFrom() reverts on nonstandard
                token behavior.
              </li>
              <br />
              <li>
                Consistent UX: Clear revert messages guide users through
                approvals.
              </li>
            </ul>
          </SectionCard>

          {/* Section 5 */}
          <SectionCard title="5. Combined Security Guarantees">
            <ul className="list-disc list-inside text-base text-gray-800">
              <li>Immutable Supply: NAU cannot be inflated.</li>
              <li>
                One-Way Conversion: Opinion tokens only arise from burning NAU.
              </li>
              <li>Role Restriction: Only Controller can burn or mint.</li>
              <li>User Consent: approve() + transferFrom() required.</li>
              <li>SafeERC20 Enforcement: All token moves revert on failure.</li>
            </ul>
          </SectionCard>
        </div>
      </div>

      {/* Conclusion */}
      <Paragraph className="text-gray-700 max-w-6xl mx-auto text-justify">
        UcWaves’ token model leverages fixed supply, controlled
        burn-to-mint logic, dedicated role-based permissions, and standardized
        allowances to deliver a tamper-resistant, transparent, and decentralized
        environment that protects user assets and upholds platform trust.
      </Paragraph>
    </div>
  </div>
);

export default SupplyControl;
